import { createContext, useContext } from 'react';
import type { Deal, DealStage } from '@elka/types';

export type CrmToastType = 'success' | 'error' | 'info';

export interface OpenLogOptions {
  contactId?: string;
  /** Logs against this deal; without it, the contact's open deal is linked. */
  dealId?: string;
}

export interface OpenNewContactOptions {
  /** Prefills the customer select. */
  companyId?: string;
}

/** App-wide actions, from CrmActionsProvider (mounted in Layout). Every function is stable. */
export interface CrmActions {
  /** One toast at a time; a new one replaces the current. Default type 'success'. */
  toast: (message: string, type?: CrmToastType) => void;
  /** Opens the quick-log dialog. */
  openLog: (type: 'call' | 'meeting', opts?: OpenLogOptions) => void;
  /** Opens the new-contact dialog. */
  openNewContact: (opts?: OpenNewContactOptions) => void;
  /** stageChangePatch + toast; No-op when unchanged. Never rejects. */
  changeStage: (deal: Deal, next: DealStage) => Promise<void>;
  /** Starts (days) or clears (null) a wait + toast. Never rejects. */
  setWaiting: (deal: Deal, days: number | null) => Promise<void>;
}

export const CrmActionsContext = createContext<CrmActions | null>(null);

export function useCrmActions(): CrmActions {
  const actions = useContext(CrmActionsContext);
  if (!actions) throw new Error('useCrmActions must be used inside CrmActionsProvider');
  return actions;
}
